import { MessageIncoming } from '@fp/interfaces/conversational/generated-async/model/message-incoming';
import { MessageIncomingPayload } from '@fp/interfaces/conversational/generated-async/model/message-incoming-payload';
import { Participant } from '@fp/interfaces/conversational/generated-async/model/participant';
import { ParticipantPayload } from '@fp/interfaces/conversational/generated-async/model/participant-payload';
import { ConversationResponseConversationMessagesInner } from '@fp/interfaces/conversational/generated/model/conversation-response-conversation-messages-inner';
import { ConversationResponseConversationMessagesInnerPayload } from '@fp/interfaces/conversational/generated/model/conversation-response-conversation-messages-inner-payload';
import { MessageBodyCard } from '@fp/interfaces/conversational/generated/model/message-body-card';
import { MessageBodyCardActionOneOf1 } from '@fp/interfaces/conversational/generated/model/message-body-card-action-one-of1';
import { MessageBodyChoicePrompt } from '@fp/interfaces/conversational/generated/model/message-body-choice-prompt';
import { MessageBodyChoicePromptChoicesInner } from '@fp/interfaces/conversational/generated/model/message-body-choice-prompt-choices-inner';
import { MessageBodyText } from '@fp/interfaces/conversational/generated/model/message-body-text';
import {
    ChatMessageModel,
    ChoiceOptionModel,
    ChoicePromptMessageModel,
    CTAMessageModel,
    MessageStatusModel,
    ParticipantMessageModel,
    PlainTextMessageModel
} from '@klant/conversational-connector/models/ChatMessage.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';

import { Injectable } from '@angular/core';

/**
 * @module KlantConversationalModule
 * @ngdoc mapper-service
 * @name ChatModelMapper
 */
@Injectable({ providedIn: 'root' })
export class ChatModelMapper {
    private static readonly MESSAGE_STATUS_RECEIVED = 'received';
    private static readonly MESSAGE_STATUS_READ = 'read';

    public static mapMessageToChatMessageModel(
        message: ConversationResponseConversationMessagesInner | MessageIncoming
    ): ChatMessageModel | null {
        const messageType = message.type;
        const payload = message.payload;

        if (messageType === MessageIncoming.TypeEnum.Message && payload.type === MessageIncomingPayload.TypeEnum.Text) {
            return this.mapMessagePayloadToPlainTextMessageModel(payload);
        }

        if (messageType === MessageIncoming.TypeEnum.Message && payload.type === MessageIncomingPayload.TypeEnum.Card) {
            return this.mapMessagePayloadToCTAMessageModel(payload);
        }

        if (messageType === MessageIncoming.TypeEnum.Message && payload.type === MessageIncomingPayload.TypeEnum.ChoicePrompt) {
            return this.mapMessagePayloadToChoicePromptMessageModel(payload);
        }

        if (messageType === Participant.TypeEnum.Participant) {
            return this.mapParticipantPayloadToParticipantMessageModel(payload as ConversationResponseConversationMessagesInnerPayload);
        }

        return null;
    }

    private static mapMessagePayloadToPlainTextMessageModel(
        payload: ConversationResponseConversationMessagesInnerPayload | MessageIncomingPayload
    ): PlainTextMessageModel {
        return {
            type: TypeModel.PLAIN_TEXT_MESSAGE,
            id: payload.id,
            text: (payload.body as MessageBodyText).text,
            fromClient: payload.fromClient,
            serviceName: payload.service?.name,
            transactionId: payload.transactionId,
            messageStatus: this.mapMessageStatusToMessageStatusModel(payload.messageStatus),
            occurredAt: payload.occurredAt
        };
    }

    private static mapMessagePayloadToChoicePromptMessageModel(
        payload: ConversationResponseConversationMessagesInnerPayload | MessageIncomingPayload
    ): ChoicePromptMessageModel {
        const body = payload.body as MessageBodyChoicePrompt;

        return {
            type: TypeModel.CHOICE_PROMPT_MESSAGE,
            id: payload.id,
            choices: this.mapChoiceMessageBodyChoicePromptChoicesInnerToChoiceOptionModel(body.choices),
            promptText: body.prompt.text,
            fromClient: payload.fromClient,
            transactionId: payload.transactionId,
            messageStatus: this.mapMessageStatusToMessageStatusModel(payload.messageStatus),
            occurredAt: payload.occurredAt
        };
    }

    private static mapMessagePayloadToCTAMessageModel(
        payload: ConversationResponseConversationMessagesInnerPayload | MessageIncomingPayload
    ): CTAMessageModel {
        const body = payload.body as MessageBodyCard;

        return {
            type: TypeModel.CTA_MESSAGE,
            id: payload.id,
            buttonLink: (body.action as MessageBodyCardActionOneOf1).link,
            buttonText: body.buttonText,
            fromClient: payload.fromClient,
            transactionId: payload.transactionId,
            messageStatus: this.mapMessageStatusToMessageStatusModel(payload.messageStatus),
            occurredAt: payload.occurredAt
        };
    }

    public static mapParticipantPayloadToParticipantMessageModel(
        payload: ConversationResponseConversationMessagesInnerPayload | ParticipantPayload
    ): ParticipantMessageModel {
        return {
            type: TypeModel.PARTICIPANT_MESSAGE,
            id: payload.id,
            name: payload.participant.name,
            serviceName: payload.participant.service.name,
            fromClient: payload.fromClient,
            introductionText: payload.participant.introduction,
            messageStatus: this.mapMessageStatusToMessageStatusModel(payload.messageStatus),
            transactionId: payload.transactionId,
            occurredAt: payload.occurredAt
        };
    }

    private static mapMessageStatusToMessageStatusModel(messageStatus: string): MessageStatusModel {
        switch (messageStatus) {
            case this.MESSAGE_STATUS_READ:
                return MessageStatusModel.READ;
            case this.MESSAGE_STATUS_RECEIVED:
                return MessageStatusModel.RECEIVED;
            default:
                return null;
        }
    }

    private static mapChoiceMessageBodyChoicePromptChoicesInnerToChoiceOptionModel(
        messageBodyChoicePromptChoicesInner: MessageBodyChoicePromptChoicesInner[]
    ): ChoiceOptionModel[] {
        return messageBodyChoicePromptChoicesInner.map((choicePrompt) => {
            return { id: choicePrompt.id, text: choicePrompt.text };
        });
    }
}
